/**
 * Cruscotto Commesse — la sezione che risponde a «come stiamo messi?».
 *
 * Nessuna tabella di commesse: solo i numeri d'insieme, e **ognuno è una
 * porta** che apre il Portafoglio già filtrato o il Magazzino. È la condizione
 * che distingue un cruscotto da una vetrina — e di vetrine, in questo progetto,
 * ne abbiamo già chiuse due (il Cruscotto del #9 e la dashboard logistica).
 */
import { AlertTriangle, ArrowRight, Boxes, PhoneCall } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { M1Gauge } from "@/components/M1Gauge";
import { DoveLaMerceFotografia } from "@/components/m1/DoveLaMerceBarra";
import { RigaTesta } from "@/components/RigaTesta";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useM1ArticoliCritici,
  useM1ClientiMerceFerma,
  useM1Dashboard,
  useM1LeadTime,
} from "@/hooks/useM1";
import { num } from "@/lib/numeri";
import { cn } from "@/lib/utils";
import type { M1DashboardKPIs, M1LeadTime, M1StatoCount } from "@/types/api";

/**
 * ⚠️ Il target **non si cabla qui**: arriva da `/m1/lead-time`.
 *
 * Stava in tre posti con due valori — l'endpoint diceva 14, questa pagina cablava
 * 21, il widget di M4 scriveva «≤ 21g» come stringa fissa — e la pagina ignorava
 * quello che l'API mandava: cambiando la soglia nel servizio, la pagina non se ne
 * accorgeva. Ora il numero sta nel servizio e la pagina lo legge.
 */

const STATI = [
  { value: "00 - EVADIBILE PUNTUALE", label: "Puntuali", tono: "ok" },
  { value: "01 - EVADIBILE IN RITARDO", label: "In ritardo", tono: "warn" },
  { value: "02 - PARZIALMENTE EVADIBILE", label: "Parziali", tono: "warn2" },
  { value: "03 - TOTALMENTE INEVADIBILE", label: "Inevadibili", tono: "alert" },
] as const;

const fmt = num;

function byStato(lista: M1StatoCount[] | undefined, stato: string): number {
  return lista?.find((s) => s.stato === stato)?.count ?? 0;
}

/**
 * Un numero che è una porta. Il testo sotto dice **dove porta**: senza quello
 * il riquadro sembra una statistica e nessuno ci clicca.
 */
function Porta({
  label,
  valore,
  dove,
  tono,
  icona: Icona,
  onClick,
  loading,
  inerte,
}: {
  label: string;
  valore: number | string | undefined;
  dove: string;
  tono?: "ok" | "warn" | "warn2" | "alert";
  icona?: LucideIcon;
  onClick?: () => void;
  loading?: boolean;
  inerte?: boolean;
}) {
  const colore =
    tono === "ok"
      ? "text-emerald-700 dark:text-emerald-400"
      : tono === "warn"
        ? "text-amber-700 dark:text-amber-400"
        : tono === "warn2"
          ? "text-orange-700 dark:text-orange-400"
          : tono === "alert"
            ? "text-red-700 dark:text-red-400"
            : "text-foreground";

  return (
    <button
      type="button"
      onClick={inerte ? undefined : onClick}
      disabled={inerte}
      className={cn(
        "group rounded-lg border bg-card p-3 text-left transition-colors",
        inerte ? "cursor-default opacity-70" : "hover:border-brand-600 hover:bg-brand-50/50 dark:hover:bg-brand-950/30"
      )}
    >
      <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
        {Icona && <Icona className="size-3" aria-hidden="true" />}
        {label}
      </span>
      {loading ? (
        <Skeleton className="my-1 h-7 w-12" />
      ) : (
        <span className={cn("block text-2xl font-semibold leading-tight tabular-nums", colore)}>
          {typeof valore === "number" ? fmt(valore) : (valore ?? "—")}
        </span>
      )}
      <span className="mt-0.5 flex items-center gap-1 text-[10px] text-brand-700 dark:text-brand-400">
        {dove}
        {!inerte && (
          <ArrowRight className="size-2.5 opacity-0 transition-opacity group-hover:opacity-100" />
        )}
      </span>
    </button>
  );
}

/**
 * Chi tiene ferma la merce già pronta.
 *
 * Risponde alla domanda della direzione — quanto magazzino occupa quel cliente —
 * e non esisteva in nessuna schermata: il dato viveva riga per riga.
 * `quota ferma` distingue il cliente grande da chi ci usa come magazzino.
 */
function ClientiMerceFerma() {
  const { data, isLoading } = useM1ClientiMerceFerma(6);
  const navigate = useNavigate();

  return (
    <section className="rounded-lg border bg-card p-3">
      <h2 className="mb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        Chi tiene ferma la merce già pronta
      </h2>
      {isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : !data?.items.length ? (
        <p className="text-xs text-muted-foreground">
          Nessuna merce pronta in attesa di ritiro.
        </p>
      ) : (
        <>
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="text-[10.5px] text-muted-foreground">
                <th className="pb-1.5 text-left font-medium">Cliente</th>
                <th className="pb-1.5 text-right font-medium">Pronta, non ritirata</th>
                <th className="pb-1.5 text-right font-medium">Commesse</th>
                <th className="pb-1.5 pl-3 text-left font-medium">Quota ferma</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {data.items.map((c) => (
                <tr
                  key={c.cliente_codice}
                  className="cursor-pointer hover:bg-accent/40"
                  onClick={() =>
                    navigate(
                      `/moduli/order-readiness?tab=portafoglio&cliente=${c.cliente_codice}`
                    )
                  }
                  title="Apre il portafoglio di questo cliente"
                >
                  <td className="py-1.5 font-medium">{c.cliente_ragione_sociale ?? "—"}</td>
                  <td className="py-1.5 text-right font-semibold tabular-nums">
                    {fmt(c.magazzino)}
                  </td>
                  <td className="py-1.5 text-right tabular-nums text-muted-foreground">
                    {c.n_commesse}
                  </td>
                  <td className="py-1.5 pl-3">
                    <div className="h-1.5 w-full overflow-hidden rounded-sm bg-muted">
                      <span
                        className="block h-full bg-emerald-600 dark:bg-emerald-500"
                        style={{ width: `${Math.min(c.quota_ferma_pct, 100)}%` }}
                      />
                    </div>
                    <span className="text-[10px] text-muted-foreground">
                      {Math.round(c.quota_ferma_pct)}% del suo ordinato
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-2 text-[11px] text-muted-foreground">
            Merce già venduta, prodotta e a magazzino: occupa spazio nostro finché il cliente
            non la ritira.
          </p>
        </>
      )}
    </section>
  );
}

/**
 * Il lead time **diviso per stato di ritiro**, che separa due problemi diversi:
 * una commessa a chiamata ha la merce pronta e aspetta il cliente, quindi il suo
 * tempo misura politica commerciale e non produzione.
 *
 * Misurato il 18-08 prima di costruirlo: delle 71 commesse in scala, 32 hanno il
 * 100% del residuo già a magazzino e stanno a 78 g; le 39 che aspettano davvero
 * qualcosa stanno a 47 g. Il numero unico diceva 64.
 */
function LeadTimePerStato({ leadTime }: { leadTime?: M1LeadTime }) {
  const righe = (leadTime?.per_stato_ritiro ?? []).filter((r) => r.n_ordini > 0);
  if (righe.length < 2) return null;
  return (
    <div className="mt-2 space-y-1 border-t pt-2">
      <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        per stato di ritiro
      </p>
      {righe.map((r) => (
        <div key={r.stato} className="flex items-baseline justify-between gap-2 text-[11px]">
          <span className="text-muted-foreground">{ETICHETTA_RITIRO[r.stato] ?? r.stato}</span>
          <span className="tabular-nums">
            {r.lt_mediana_giorni === null ? (
              <span className="text-muted-foreground/70" title="Nessuna commessa misurabile: tutte hanno una riga inevadibile">
                non misurabile
              </span>
            ) : (
              <>
                <b>{fmt(r.lt_mediana_giorni)}g</b>{" "}
                <span className="text-muted-foreground">su {r.n_ordini}</span>
              </>
            )}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * Quanto magazzino occupa ciascuno stato di ritiro. È la risposta alla domanda di
 * Enzo che il numero unico non poteva dare: dei pezzi fermi, quanti sono **a
 * chiamata** — cioè il servizio che vendiamo, legittimo — e quanti no, che è un
 * problema. Finché nessuno marca, sta tutto in «standard» e il riquadro lo dice.
 */
function MagazzinoPerStato({ dashboard }: { dashboard?: M1DashboardKPIs }) {
  const righe = (dashboard?.magazzino_per_stato_ritiro ?? []).filter(
    (r) => Number(r.magazzino) > 0,
  );
  if (!righe.length) return null;
  const totale = righe.reduce((a, r) => a + Number(r.magazzino), 0) || 1;
  return (
    <section className="rounded-lg border bg-card p-3">
      <h2 className="mb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        La merce ferma, per stato di ritiro{" "}
        <span className="normal-case tracking-normal text-muted-foreground/80">
          — quanta è il servizio che vendiamo e quanta è un problema
        </span>
      </h2>
      <div className="flex h-7 gap-0.5 overflow-hidden rounded">
        {righe.map((r) => (
          <div
            key={r.stato}
            title={`${ETICHETTA_RITIRO[r.stato] ?? r.stato}: ${fmt(r.magazzino)} pezzi su ${r.commesse} commesse`}
            style={{ flex: Number(r.magazzino) / totale }}
            className={cn(
              "flex min-w-0 items-center justify-center overflow-hidden whitespace-nowrap px-2 text-[10.5px] font-medium",
              r.stato === "a_chiamata"
                ? "bg-orange-200 text-orange-900 dark:bg-orange-900 dark:text-orange-100"
                : r.stato === "tassativa"
                  ? "bg-emerald-200 text-emerald-900 dark:bg-emerald-900 dark:text-emerald-100"
                  : "bg-muted text-muted-foreground",
            )}
          >
            {fmt(r.magazzino)}
          </div>
        ))}
      </div>
      <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
        {righe.map((r) => (
          <span key={r.stato}>
            {ETICHETTA_RITIRO[r.stato] ?? r.stato}: <b className="text-foreground">{fmt(r.magazzino)}</b>{" "}
            pezzi · {r.commesse} commesse
          </span>
        ))}
        {righe.length === 1 && righe[0].stato === "standard" && (
          <span className="italic">
            nessuna commessa è ancora marcata «a chiamata»: finché non lo è, la merce
            ferma non si può distinguere
          </span>
        )}
      </div>
    </section>
  );
}

const ETICHETTA_RITIRO: Record<string, string> = {
  tassativa: "tassative",
  standard: "standard",
  a_chiamata: "a chiamata",
};

export function CruscottoM1({ dashboard }: { dashboard?: M1DashboardKPIs }) {
  const navigate = useNavigate();
  const { isLoading: loadingDash } = useM1Dashboard();
  const { data: leadTime, isLoading: loadingLT } = useM1LeadTime();
  const { data: critici } = useM1ArticoliCritici();

  const senzaPiano = critici?.filter((a) => Number(a.piano_pronto_in_tempo) === 0).length;

  const vaiAlPortafoglio = (params: string) =>
    navigate(`/moduli/order-readiness?tab=portafoglio&${params}`);

  return (
    <div className="space-y-3">
      <RigaTesta
        caricatoIl={dashboard?.snapshot_caricato_il}
        destra="aggiornato all'apertura"
      />

      <DoveLaMerceFotografia merce={dashboard?.dove_la_merce} />

      <section className="grid grid-cols-2 gap-2 lg:grid-cols-4">
        {STATI.map((s) => (
          <Porta
            key={s.value}
            label={s.label}
            valore={byStato(dashboard?.ordini_per_stato_maitime, s.value)}
            dove="apre il portafoglio"
            tono={s.tono}
            loading={loadingDash}
            onClick={() => vaiAlPortafoglio(`stato=${encodeURIComponent(s.value)}`)}
          />
        ))}
      </section>

      <div className="grid gap-3 lg:grid-cols-[248px_1fr]">
        <div className="h-fit rounded-lg border bg-card p-3">
          <h2 className="mb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            Lead time
          </h2>
          {loadingLT || !leadTime ? (
            <Skeleton className="h-32 w-full" />
          ) : (
            <M1Gauge
              valore={leadTime.lt_mediana_giorni}
              target={leadTime.target_giorni}
              sogliaGiallo={leadTime.soglia_giallo}
              sogliaRosso={leadTime.soglia_rosso}
              unitaLabel={`su ${fmt(leadTime.n_ordini_in_scala)} commesse di ${fmt(leadTime.n_ordini_aperti)}`}
            />
          )}
          {leadTime && leadTime.n_ordini_fuori_scala > 0 && (
            /* Il denominatore, che prima non usciva dall'API e quindi la pagina
               non poteva dirlo: le commesse con almeno una riga inevadibile sono
               escluse *per costruzione*, quindi più le cose vanno male meno
               commesse entrano nel conto. */
            <p className="mt-2 border-t pt-2 text-[10.5px] leading-snug text-muted-foreground">
              <b>{fmt(leadTime.n_ordini_fuori_scala)}</b> commesse restano fuori dal
              conto: hanno almeno una riga inevadibile, quindi non hanno una data in
              cui la merce sarà pronta.
            </p>
          )}
          <LeadTimePerStato leadTime={leadTime} />
        </div>
        <ClientiMerceFerma />
      </div>

      <MagazzinoPerStato dashboard={dashboard} />

      <section className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <Porta
          label="Discordanze col gestionale"
          valore={dashboard?.ordini_in_divergenza}
          dove="apre il portafoglio"
          icona={AlertTriangle}
          loading={loadingDash}
          onClick={() => vaiAlPortafoglio("discordanze=1")}
        />
        <Porta
          label="Articoli senza piano"
          valore={senzaPiano}
          dove="apre il magazzino"
          tono="alert"
          icona={Boxes}
          loading={senzaPiano === undefined}
          // Ora la porta atterra sul filtro giusto: mostrava 250 e portava su una
          // lista di 315, perché il Magazzino teneva il filtro in `useState`.
          onClick={() => navigate("/moduli/order-readiness?tab=magazzino&magazzino=senza_piano")}
        />
        {/* Era «Commesse a chiamata», che restava a zero perché il gesto per
            marcarle non esisteva in nessuna interfaccia. Ora quel conteggio sta
            nella fila dei riquadri del Portafoglio, dove filtra davvero, e qui
            c'è il segnale che nessuna schermata mostrava: un ritiro fissato con
            la data già passata. */}
        <Porta
          label="Ritiri scaduti"
          valore={dashboard?.ordini_ritiro_scaduto}
          dove={
            dashboard?.ordini_ritiro_scaduto ? "apre il portafoglio" : "nessuno scaduto"
          }
          tono={dashboard?.ordini_ritiro_scaduto ? "alert" : undefined}
          icona={PhoneCall}
          loading={loadingDash}
          inerte={!dashboard?.ordini_ritiro_scaduto}
          onClick={() => vaiAlPortafoglio("ritiro_scaduto=1")}
        />
      </section>
    </div>
  );
}
